/**
 * Product analytics (PostHog) — thin wrapper.
 *
 * Frontend visitor analytics only (marketing site + dashboard). Deliberately
 * separate from the QR scan/redirect analytics pipeline (backend/app/api/redirect.py),
 * which tracks our customers' end-users, not our own visitors — see
 * docs/architecture.md and issue #57.
 *
 * No-ops everywhere when REACT_APP_POSTHOG_KEY is unset, so local dev/CI
 * never talk to PostHog and never throw.
 *
 * posthog-js itself (~79kB gzipped) is loaded via dynamic import() rather
 * than a static import, so it lands in its own webpack chunk instead of
 * CRA's main bundle — a static import here measurably slowed first paint,
 * since every visitor's browser had to download/parse/execute PostHog
 * before the app could render at all. Calls made before the chunk finishes
 * loading are queued and flushed once it's ready.
 */
import type { PostHog } from 'posthog-js';
import type { User } from '../types';

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY || '';
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

let posthogInstance: PostHog | null = null;
let loadStarted = false;
const queue: Array<(ph: PostHog) => void> = [];

function init(): void {
  if (!POSTHOG_KEY || loadStarted) return;
  loadStarted = true;

  import('posthog-js').then(({ default: posthog }) => {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      capture_pageview: false, // fired manually on route change, see RouteTracker
      capture_pageleave: true,
      person_profiles: 'identified_only',
      disable_session_recording: true,
    });

    posthogInstance = posthog;
    queue.splice(0).forEach((fn) => fn(posthog));
  });
}

// Any call can be the first one made (e.g. RouteTracker's mount-time
// pageview fires before App's own effect, since child effects run before
// parent effects) — so every entry point lazily kicks off the load itself
// rather than relying solely on the explicit init() call in App.tsx.
function run(fn: (ph: PostHog) => void): void {
  if (!POSTHOG_KEY) return;
  if (posthogInstance) {
    fn(posthogInstance);
    return;
  }
  init();
  queue.push(fn);
}

function pageview(path: string): void {
  run((ph) => ph.capture('$pageview', { $current_url: path }));
}

function identify(user: User): void {
  run((ph) =>
    ph.identify(String(user.id), {
      email: user.email,
      plan: user.subscription?.plan?.name,
    })
  );
}

function reset(): void {
  run((ph) => ph.reset());
}

function capture(event: string, properties?: Record<string, unknown>): void {
  run((ph) => ph.capture(event, properties));
}

export const analytics = {
  init,
  pageview,
  identify,
  reset,
  capture,
};
